/**
 * Network-scoped managed vector index pipeline (ingest + cold search).
 * GET /networks/:id/memory-stream/summary
 */
import React, { useState, useEffect, useCallback, useMemo, useId } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { motion } from 'framer-motion';
import { RefreshCw, Waves } from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { RefreshesOnLoadBadge } from './PlatformBillingPanel';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

const STATUS_STYLES = {
  ok: {
    ring: 'ring-emerald-500/30',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    bar: 'from-emerald-500 to-teal-400',
  },
  warn: {
    ring: 'ring-amber-500/40',
    dot: 'bg-amber-500',
    text: 'text-amber-800',
    bar: 'from-amber-500 to-orange-400',
  },
  unknown: {
    ring: 'ring-gray-300',
    dot: 'bg-gray-400',
    text: 'text-gray-600',
    bar: 'from-gray-400 to-gray-300',
  },
};

function Sparkline({ series, colorClass = 'stroke-blue-600', height = 72, fillGradientId }) {
  const pts = useMemo(() => {
    if (!series?.length) return null;
    const vals = series.map((p) => Number(p.v) || 0);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = 4;
    const w = 320;
    const h = height;
    const range = max - min || 1;
    return series.map((p, i) => {
      const x = pad + (i / Math.max(series.length - 1, 1)) * (w - pad * 2);
      const y = h - pad - ((Number(p.v) - min) / range) * (h - pad * 2);
      return `${x},${y}`;
    });
  }, [series, height]);

  if (!pts?.length) {
    return (
      <div
        className="flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg bg-gray-50/80"
        style={{ height }}
      >
        No time series yet
      </div>
    );
  }

  const d = `M ${pts.join(' L ')}`;
  const fillId = fillGradientId || 'sfStreamFill';
  return (
    <svg
      viewBox="0 0 320 72"
      className="w-full max-w-md"
      preserveAspectRatio="none"
      style={{ height }}
      aria-hidden
    >
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(37 99 235)" stopOpacity="0.2" />
          <stop offset="100%" stopColor="rgb(37 99 235)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L 316 68 L 4 68 Z`} fill={`url(#${fillId})`} className="opacity-90" />
      <path d={d} fill="none" className={colorClass} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function FlowArrow() {
  return (
    <div className="flex items-center justify-center px-1 sm:px-2 shrink-0" aria-hidden>
      <svg width="28" height="12" viewBox="0 0 28 12" className="text-blue-400/90">
        <motion.path
          d="M0 6h20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          initial={{ pathLength: 0.3, opacity: 0.4 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.2, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
        />
        <path d="M20 2l6 4-6 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

/**
 * @param {{ network: object, layout: 'page' | 'nested' }} props
 */
export default function MemoryVectorDbPanel({ network, layout = 'page' }) {
  const { user } = useAuth();
  const networkId = network?.networkId;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const fillIngest = `sfIngest_${useId().replace(/:/g, '_')}`;
  const fillSearch = `sfSearch_${useId().replace(/:/g, '_')}`;

  const fetchSummary = useCallback(async () => {
    if (!networkId || networkId === 'new') return;
    setLoading(true);
    setError(null);
    try {
      const idToken = getRestApiBearerToken();
      const response = await fetch(`${API_BASE_URL}/networks/${networkId}/memory-stream/summary`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || body.message || response.statusText);
      }
      setData(body);
    } catch (e) {
      setError(e.message || 'Failed to load stream summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [networkId, user?.idToken]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const pipeline = data?.pipeline || [];
  const kpis = data?.kpis || {};
  const ingestSeries = data?.series?.ingestQueueOldestAgeSeconds || [];
  const searchSeries =
    data?.series?.memorySearchDurationAverageMs || data?.series?.memorySearchDurationP99Ms || [];

  const isPage = layout === 'page';
  const shellClass = isPage
    ? 'h-full overflow-y-auto bg-gradient-to-b from-slate-50/80 to-white'
    : '';
  const innerClass = isPage ? 'max-w-6xl mx-auto px-6 py-8' : 'pt-1';

  if (!networkId || networkId === 'new') {
    return (
      <div
        className={`flex flex-col items-center justify-center text-gray-500 p-8 ${isPage ? 'h-full' : 'min-h-[200px]'}`}
      >
        <Waves className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm text-center max-w-sm">
          Save your network first to view vector DB pipeline health.
        </p>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <div className={innerClass}>
        <div className={`flex flex-wrap items-start justify-between gap-4 ${isPage ? 'mb-8' : 'mb-6'}`}>
          <div>
            {isPage ? (
              <>
                <div className="flex flex-wrap items-center gap-2 gap-y-1 text-blue-700 mb-1">
                  <div className="flex items-center gap-2">
                    <Waves className="w-5 h-5" aria-hidden />
                    <span className="text-xs font-semibold uppercase tracking-wider">Memory pipeline</span>
                  </div>
                  <RefreshesOnLoadBadge />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Vector DB (managed) · live pipeline</h2>
                <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                  Ingest and search flow into the managed vector index. Charts use shared environment metrics and{' '}
                  <strong className="font-medium text-gray-600">refresh when you load or reload</strong>—not on the daily
                  storage snapshot schedule. Open <strong className="font-medium text-gray-600">Usage → Vector DB (managed)</strong>{' '}
                  for this view next to capacity metering.
                </p>
              </>
            ) : (
              <div>
                <div className="flex flex-wrap items-center gap-2 gap-y-1 text-indigo-700 mb-1">
                  <div className="flex items-center gap-2">
                    <Waves className="w-4 h-4" aria-hidden />
                    <span className="text-xs font-semibold uppercase tracking-wider">Vector DB (managed)</span>
                  </div>
                  <RefreshesOnLoadBadge />
                </div>
                <p className="text-sm text-gray-600 max-w-2xl">
                  Managed vector index — ingest buffer, indexer, and cold search latency (shared environment metrics). This is
                  not the same as daily storage gauge snapshots under Capacity &amp; metering.
                </p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={fetchSummary}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {data?.cloudwatchLastError && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-950 font-mono break-all">
            <span className="font-semibold text-amber-900">CloudWatch API: </span>
            {data.cloudwatchLastError}
          </div>
        )}

        {data?.metricsScopeNote && (
          <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3 text-xs text-blue-900 leading-relaxed">
            <span className="font-semibold">Scope: </span>
            {data.metricsScopeNote}
          </div>
        )}

        {loading && !data && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-sm">Loading pipeline metrics…</p>
          </div>
        )}

        {!(loading && !data) && (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
          {[
            {
              label: 'Queue oldest message (max in window)',
              value:
                kpis.ingestQueueMaxAgeSeconds != null
                  ? `${Number(kpis.ingestQueueMaxAgeSeconds).toFixed(1)}s`
                  : '—',
              sub: 'SQS FIFO ingest buffer (env)',
            },
            {
              label: 'Processor errors (≈1h)',
              value: kpis.processorErrorsLastHour != null ? String(kpis.processorErrorsLastHour) : '—',
              sub: 'memory-stream-processor',
            },
            {
              label: 'memory-search errors (≈1h)',
              value: kpis.memorySearchErrorsLastHour != null ? String(kpis.memorySearchErrorsLastHour) : '—',
              sub: 'Cold vector search Lambda',
            },
          ].map((k) => (
            <div key={k.label} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k.label}</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">{k.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{k.sub}</p>
            </div>
          ))}
        </div>

        <div className="mb-10 rounded-xl border border-gray-100 bg-white p-6 shadow-sm overflow-x-auto">
          <h3 className="text-sm font-semibold text-gray-800 mb-6">Live pipeline</h3>
          <div className="flex flex-wrap items-stretch justify-center gap-y-4 min-w-[min(100%,720px)]">
            {pipeline.map((step, idx) => {
              const st = STATUS_STYLES[step.status] || STATUS_STYLES.unknown;
              return (
                <React.Fragment key={step.id}>
                  {idx > 0 && <FlowArrow />}
                  <motion.div
                    layout
                    className={`flex-1 min-w-[120px] max-w-[160px] rounded-xl border border-gray-100 p-4 ring-2 ${st.ring} bg-gradient-to-b from-white to-slate-50/90`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`h-2 w-2 rounded-full ${st.dot}`} />
                      <span className={`text-[10px] font-bold uppercase tracking-wide ${st.text}`}>
                        {step.status}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 leading-tight">{step.label}</p>
                    <p className="text-[11px] text-gray-500 mt-1 leading-snug break-words">{step.subtitle}</p>
                    <div
                      className={`mt-3 h-1 rounded-full bg-gradient-to-r ${st.bar} opacity-80`}
                      aria-hidden
                    />
                  </motion.div>
                </React.Fragment>
              );
            })}
          </div>
          {data?.updatedAt && (
            <p className="text-[11px] text-gray-400 mt-4 text-center">
              Updated {new Date(data.updatedAt).toLocaleString()}
              {!data.cloudwatchAvailable && ' · CloudWatch unavailable — check API IAM or region'}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Ingest lag estimate</h3>
            <p className="text-xs text-gray-500 mb-4">
              Max age of oldest message in FIFO queue (1-min buckets, last 24h)
            </p>
            <Sparkline series={ingestSeries} colorClass="stroke-emerald-600" fillGradientId={fillIngest} />
            {ingestSeries.length > 0 && ingestSeries.every((p) => Number(p.v) === 0) && (
              <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                Flat at 0s: no visible backlog in this window (FIFO kept empty / consumer keeping up).
              </p>
            )}
          </div>
          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Cold search duration (avg)</h3>
            <p className="text-xs text-gray-500 mb-4">
              memory-search Lambda Average Duration (1-min buckets, last 24h)
            </p>
            <Sparkline series={searchSeries} colorClass="stroke-indigo-600" fillGradientId={fillSearch} />
            {searchSeries.length > 0 && searchSeries.length <= 5 && (
              <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                Few points: CloudWatch only records minutes where memory-search ran (sparse is normal).
              </p>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
